import React, { useState } from "react";
import { Icon } from "@/components/Icon";
import { tileAspectClass } from "@/lib/casinoClassification";
import iconMark from "../../assets/pnrbet-icon.png";

/* CasinoTile — the premium casino game tile from the "03 Casino" redesign (CA-01).
   Portrait 3:4 card with full-bleed game art, a small provider / "PRYMEBET Original"
   badge top-left, an optional HOT / NEW / LIVE tag top-right, the game name over a
   bottom gradient, and a hover lift with a subtle NEUTRAL black elevation (no green
   glow — the design authority mandates neutral elevation). A `live` variant is a
   wider landscape card for live-dealer tables. Tiles without art render a polished
   branded fallback (P-mark + real game name on a neutral gradient). */

const TAG_STYLE = {
  HOT: "bg-[#EF4444] text-white",
  NEW: "bg-[#00D632] text-[#0A0A0A]",
  LIVE: "bg-[#EF4444] text-white",
};

export default function CasinoTile({
  name,
  img,
  badge = "PRYMEBET Original",
  tag = null, // "HOT" | "NEW" | "LIVE" | null
  provider = false, // true => provider badge style (neutral) instead of green Original
  playing = null, // honest live-players count; null = hide
  live = false, // landscape live-dealer variant
  square = false, // 1:1 tile — for provider art that ships square (e.g. JackTop 500x500)
  onClick,
  testid,
}) {
  const [broken, setBroken] = useState(false);
  const showArt = img && !broken;
  // The Original badge already carries the P-mark logo (= the PRYMEBET brand), so the word
  // "PRYMEBET" in front of "Original" is redundant and, on the narrow tiles, forced truncation
  // next to a HOT/NEW tag. Drop the leading brand word for Original tiles (the icon conveys it);
  // provider badges (e.g. "JackTop") keep their full name.
  const badgeLabel = provider ? badge : badge.replace(/^PRYMEBET\s+/i, "");

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`group relative block w-full overflow-hidden rounded-2xl text-left
        bg-[linear-gradient(145deg,hsl(var(--ink-700)),hsl(var(--surface-3))_60%,hsl(var(--ink-800)))]
        ring-1 ring-white/[0.06]
        shadow-[inset_0_1px_0_hsl(var(--edge-light)),var(--elev-2)]
        transition-all duration-[var(--dur-base)]
        hover:-translate-y-1.5 hover:ring-1 hover:ring-pnr-green/50
        hover:shadow-[var(--glow-green-soft),var(--elev-3)]
        active:scale-[0.98] ${tileAspectClass({ square, live })}`}
    >
      {showArt ? (
        <img
          src={img}
          alt=""
          loading="lazy"
          onError={() => setBroken(true)}
          /* object-center keeps the subject (e.g. a dealer's head) framed; a square tile
             fed square art crops nothing, and non-square art degrades gracefully. */
          className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
        />
      ) : (
        /* Branded fallback when a game ships no art (or it fails to load): a neutral
           PRYMEBET-marked gradient showing the REAL game name, not an internal "slot"
           label. (UIB-22) */
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-[linear-gradient(145deg,hsl(var(--ink-700)),hsl(var(--surface-3))_60%,hsl(var(--ink-800)))] px-3 text-center">
          <img src={iconMark} alt="" className="h-9 w-9 rounded-lg object-contain opacity-90 ring-1 ring-white/10" />
          <span className="line-clamp-2 font-display text-sm font-black leading-tight tracking-tight text-white/90">{name}</span>
        </div>
      )}

      {/* top gradient so badges stay legible on any art */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/55 to-transparent" />

      {/* top row: original/provider badge (left) + HOT/NEW/LIVE tag (right).
         A single flex row with justify-between so the badge and tag never overlap
         (the longer "PRYMEBET Original" label used to slide under the tag); the badge
         truncates only as a last resort on the narrowest tiles. */}
      <div className="absolute inset-x-2 top-2 flex items-start justify-between gap-1.5">
        <span className="inline-flex min-w-0 items-center gap-1 rounded-md bg-black/55 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] backdrop-blur-sm">
          {!provider && <img src={iconMark} alt="" className="h-3.5 w-3.5 shrink-0 rounded-[3px] object-contain" />}
          <span className={`truncate ${provider ? "text-white/85" : "text-[#00D632]"}`}>{badgeLabel}</span>
        </span>
        {tag && (
          <span className={`inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider ${TAG_STYLE[tag] || TAG_STYLE.HOT}`}>
            {tag === "LIVE" && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />}
            {tag}
          </span>
        )}
      </div>

      {/* bottom gradient + name (+ optional players). When the branded fallback is shown
         (no art) it already carries the name centered, so skip the duplicate here. */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/55 to-transparent p-3 pt-10">
        {showArt && <p className="font-display text-sm font-black leading-tight tracking-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)] sm:text-[15px]">{name}</p>}
        {playing != null && (
          <p className="mt-0.5 flex items-center gap-1 text-[10px] font-semibold text-[#a1a1aa]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#00D632]" /> {Number(playing).toLocaleString()} playing
          </p>
        )}
      </div>
    </button>
  );
}
